import { useState, ChangeEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { SignupCredentials } from "../types/userData.types";

interface FieldErrors {
  name?: string;
  email?: string;
  phone?: string;
  city?: string;
  password?: string;
}

interface SignupCardProps {
  onSubmit?: (data: SignupCredentials) => void;
  fieldErrors?: FieldErrors;
  onEmailChange?: (email: string) => void;
  setFieldErrors?: (errors: FieldErrors) => void;
  onClose?: () => void;
}

export const SignupCard = ({
  onSubmit = () => {},
  onEmailChange = () => {},
  fieldErrors = {},
  setFieldErrors = () => {},
  onClose,
}: SignupCardProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    return () => setFieldErrors({});
  }, []);

  const [formData, setFormData] = useState<SignupCredentials>({
    name: "",
    email: "",
    phone: "",
    city: "",
    password: "",
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        className="relative bg-popupLight dark:bg-popupDark shadow-lg rounded-2xl p-8 w-full max-w-md mx-4 animate-fade-in-up custom-scroll max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-2xl font-bold text-center text-primaryText dark:text-primaryTextDark mb-4">
          {t("signup")}
        </h2>
        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primaryText dark:text-primaryTextDark">
              {t("name")}
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700 dark:text-primaryTextDark  focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-secondaryTextDark"
            />
            <span
              className={`text-sm text-red-500 ${
                fieldErrors.name ? "visible" : "invisible"
              }`}
            >
              {fieldErrors.name || "placeholder"}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-primaryText dark:text-primaryTextDark">
              {t("email")}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={() => onEmailChange(formData.email)}
              placeholder="Enter your email"
              className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700 dark:text-primaryTextDark  focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-secondaryTextDark"
            />
            <span
              className={`text-sm text-red-500 ${
                fieldErrors.email ? "visible" : "invisible"
              }`}
            >
              {fieldErrors.email || "placeholder"}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-primaryText dark:text-primaryTextDark">
              {t("phoneNumber")}
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter phone number"
              className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700 dark:text-primaryTextDark  focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-secondaryTextDark"
            />
            <span
              className={`text-sm text-red-500 ${
                fieldErrors.phone ? "visible" : "invisible"
              }`}
            >
              {fieldErrors.phone || "placeholder"}
            </span>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-primaryText dark:text-primaryTextDark">
              {t("city")}
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Enter your city"
              className="relative mt-1 w-full px-4 py-2 rounded-md shadow-sm bg-white dark:bg-gray-700 text-black dark:text-primaryTextDark border border-gray-300 dark:border-gray-600  focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-secondaryTextDark"
            />
            <span
              className={`text-sm text-red-500 ${
                fieldErrors.city ? "visible" : "invisible"
              }`}
            >
              {fieldErrors.city || "placeholder"}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-primaryText dark:text-primaryTextDark">
              {t("password")}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password"
              className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700 dark:text-primaryTextDark  focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-secondaryTextDark"
            />
            <span
              className={`text-sm text-red-500 ${
                fieldErrors.password ? "visible" : "invisible"
              }`}
            >
              {fieldErrors.password || "placeholder"}
            </span>
          </div>

          <button
            type="button"
            onClick={() => onSubmit(formData)}
            className="w-full bg-secondaryText dark:bg-secondaryTextDark text-primaryText py-2 px-4 rounded-md hover:bg-blue-700 transition"
          >
            {t("signup")}
          </button>
        </form>
      </div>
    </div>
  );
};
